import { COLLECTION_CURRENCIES, DEFAULT_CURRENCY, getCurrencyLabel } from "@repo/shared";
import type { CreateFlowRequest, UpdateFlowRequest } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, patch, post } from "@/lib/api/_client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { Flow } from "@/lib/types/flow";
import { T } from "./flow-dialog.i18n";

// Get localized language name using Intl.DisplayNames
function getLocalizedLanguageName(langCode: string, userLanguage: string): string {
  try {
    // Convert locale codes: en_GB -> en-GB, nb_NO -> nb-NO
    const displayLocale = userLanguage.replace("_", "-");
    const targetLocale = langCode.replace("_", "-");
    const displayNames = new Intl.DisplayNames([displayLocale], { type: "language" });
    return displayNames.of(targetLocale) || langCode;
  } catch {
    return langCode;
  }
}

type FlowDialogProps = {
  open: boolean;
  flow: Flow | null;
  supportedLanguages: string[];
  defaultLanguage: string;
  onClose: () => void;
};

export function FlowDialog({
  open,
  flow,
  supportedLanguages,
  defaultLanguage,
  onClose,
}: FlowDialogProps) {
  const navigate = useNavigate();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const editingFlowName = flow?.name ?? "";
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    language: defaultLanguage,
    currency: DEFAULT_CURRENCY,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (flow) {
      setFormData({
        name: flow.name,
        description: flow.description || "",
        language: flow.language || defaultLanguage,
        currency: DEFAULT_CURRENCY,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        language: defaultLanguage,
        currency: DEFAULT_CURRENCY,
      });
    }
    setError(null);
  }, [flow, open, defaultLanguage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = flow
        ? await patch<{ id: string }>(`/internal/flows/${flow.id}`, {
            name: formData.name,
            description: formData.description,
          } satisfies UpdateFlowRequest)
        : await post<{ id: string }>("/internal/flows", formData satisfies CreateFlowRequest);

      // If creating, navigate to editor
      if (!flow && data.id) {
        navigate(`/settings/flows/${data.id}`);
      }

      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t.failedToSaveFlow);
      } else {
        setError(t.failedToSaveFlow);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {flow
              ? editingFlowName
                ? `${t.editFlow} (${editingFlowName})`
                : t.editFlow
              : t.createNewFlow}
          </DialogTitle>
          <DialogDescription>
            {flow ? t.editFlowDescription : t.createFlowDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="flow-name">
                {t.flowName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="flow-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.flowNamePlaceholder}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t.description}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
                rows={3}
              />
            </div>

            {/* Language selector - only shown when creating */}
            {!flow && (
              <div className="space-y-2">
                <Label htmlFor="language">
                  {t.language} <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData({ ...formData, language: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectLanguage} />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedLanguages.map((lang) => (
                      <SelectItem key={lang} value={lang}>
                        {getLocalizedLanguageName(lang, userLanguage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Currency selector - only shown when creating, fixed after (QL-372) */}
            {!flow && (
              <div className="space-y-2">
                <Label htmlFor="flow-currency">{t.currency}</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger id="flow-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLECTION_CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {getCurrencyLabel(c.code, userLanguage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.currencyHint}</p>
              </div>
            )}

            {/* Show language info when editing (read-only) */}
            {flow?.language && (
              <div className="space-y-2">
                <Label>{t.language}</Label>
                <p className="text-sm text-muted-foreground">
                  {getLocalizedLanguageName(flow.language || "en_GB", userLanguage)}
                  <span className="ml-2 text-xs">({t.languageCannotBeChanged})</span>
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={loading}>
              {flow ? t.save : t.create}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
